import React, { useRef, useEffect } from 'react';
import { Platform, View, StyleSheet, ViewStyle } from 'react-native';

// Interface for individual word objects
interface Word {
  text: string;
  size: number;
  color?: string;
}

// Interface for the component props
export interface WordCloudProps {
  words?: Word[];
  width?: number;
  height?: number;
  colors?: string[] | ((word: Word) => string);
  backgroundColor?: string;
  padding?: number;
  fontFamily?: string;
  rotate?: () => number;
  onWordClick?: (word: string) => void;
  style?: ViewStyle | React.CSSProperties;
}

// Default color generator function
const defaultColors = (): ((word: Word) => string) => {
  return () => `hsl(${Math.random() * 360}, 100%, 50%)`;
};

// Create HTML content for both platforms
const createHtmlContent = (
  words: Word[],
  width: number,
  height: number,
  colors: string[] | ((word: Word) => string),
  backgroundColor: string,
  padding: number,
  fontFamily: string,
  rotate: () => number,
  onWordClick?: (word: string) => void
) => {
  // Prepare the words data
  const processedWords = words.map((word) => ({
    text: word.text,
    size: word.size,
    ...(word.color && { color: word.color }),
  }));

  return `
  <!DOCTYPE html>
  <html>
  <head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <script src="https://d3js.org/d3.v5.min.js"></script>
    <script src="https://cdnjs.cloudflare.com/ajax/libs/d3-cloud/1.2.5/d3.layout.cloud.min.js"></script>
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
    <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@100..900&display=swap" rel="stylesheet">
    <style>
      body { 
        margin: 0; 
        background-color: ${backgroundColor}; 
        overflow: hidden;
      }
      #wordcloud { 
        width: ${width}px; 
        height: ${height}px; 
      }
    </style>
  </head>
  <body>
    <div id="wordcloud"></div>
    <script>
      const words = ${JSON.stringify(processedWords)};
      const width = ${width};
      const height = ${height};
      const padding = ${padding};
      const fontFamily = '${fontFamily}';
      const rotate = ${rotate()};
      const colors = ${
        typeof colors === 'function'
          ? colors.toString()
          : JSON.stringify(colors)
      };
      // Function to check if text contains Japanese characters
      function containsJapanese(text) {
        return /[\u3000-\u303f\u3040-\u309f\u30a0-\u30ff\uff00-\uff9f\u4e00-\u9faf\u3400-\u4dbf\u31f0-\u31ff\u3220-\u3243\u3280-\u337f]/.test(text);
      }

      // Adjust the cloud layout to handle Japanese text better
      d3.layout.cloud()
        .size([width, height])
        .words(words)
        .padding(padding)
        .rotate(function(d) {
          // Optional: prevent rotation for Japanese text for better readability
          return rotate > 0 ? ~~(Math.random() * 2) * 90 : 0;
        })
        .font(function(d) {
          // Always use Noto Sans JP for Japanese text, fallback to specified font for others
          return containsJapanese(d.text) ? 'Noto Sans JP' : fontFamily;
        })
        .fontSize(function(d) {
          // Apply different scaling for Japanese vs non-Japanese text
          const baseSize = d.size * 5; // Base scaling factor
          return containsJapanese(d.text) ? baseSize * 1.5 : baseSize;
        })
        .on('end', draw)
        .start();

      function draw(words) {
        const svg = d3.select('#wordcloud').append('svg')
          .attr('width', width)
          .attr('height', height)
          .append('g')
          .attr('transform', 'translate(' + width / 2 + ',' + height / 2 + ')');

        svg.selectAll('text')
          .data(words)
          .enter().append('text')
          .style('font-size', d => d.size + 'px')
          .style('font-family', d => containsJapanese(d.text) ? 'Noto Sans JP, sans-serif' : fontFamily)
          .style('fill', d => {
            if (d.color) return d.color;
            return typeof colors === 'function' ? colors(d) : colors[Math.floor(Math.random() * colors.length)];
          })
          .attr('text-anchor', 'middle')
          .attr('transform', d => 'translate(' + [d.x, d.y] + ')rotate(' + d.rotate + ')')
          .text(d => d.text)
          ${
            onWordClick
              ? `.style('cursor', 'pointer')
                .on('click', function(d) {
                  window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'wordClick', word: d.text }));
                })`
              : ''
          };
      }
    </script>
  </body>
  </html>
  `;
};

// Split into two separate components to avoid any cross-loading
// Web Component
const WebWordClouds: React.FC<WordCloudProps> = (props) => {
  const {
    words = [],
    width = 300,
    height = 300,
    colors = defaultColors(),
    backgroundColor = 'white',
    padding = 5,
    fontFamily = 'Impact',
    rotate = () => ~~(Math.random() * 2) * 90,
    onWordClick,
    style,
  } = props;

  const iframeRef = useRef<HTMLIFrameElement>(null);

  const htmlContent = createHtmlContent(
    words,
    width,
    height,
    colors,
    backgroundColor,
    padding,
    fontFamily,
    rotate,
    onWordClick
  );

  useEffect(() => {
    if (iframeRef.current) {
      // Add message event listener for iframe communication
      const handleIframeMessage = (event: MessageEvent) => {
        if (event.data && event.data.type === 'wordClick' && onWordClick) {
          onWordClick(event.data.word);
        }
      };

      window.addEventListener('message', handleIframeMessage);

      return () => {
        window.removeEventListener('message', handleIframeMessage);
      };
    }
  }, [onWordClick]);

  return (
    <iframe
      ref={iframeRef}
      srcDoc={htmlContent}
      style={{
        width: width,
        height: height,
        border: 'none',
        backgroundColor,
        ...(style as React.CSSProperties),
      }}
      title="Word Cloud"
    />
  );
};

const WebWordCloud: React.FC<WordCloudProps> = (props) => {
  if (Platform.OS === 'web') {
    return <WebWordClouds {...props} />;
  } else {
    return <></>;
  }
};

const styles = StyleSheet.create({
  container: {
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default WebWordCloud;
